import React, { useEffect, useState } from 'react';
import { providerCompaniesAPI, usersAPI, authAPI } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Building, Users, Mail, Plus, UserPlus, ToggleLeft, ToggleRight, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

export default function ProvidersPage() {
  const [providers, setProviders] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [expandedProvider, setExpandedProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Dialogs
  const [showCreateProvider, setShowCreateProvider] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [selectedProviderId, setSelectedProviderId] = useState(null);
  
  // Forms
  const [newProvider, setNewProvider] = useState({ name: '', tax_id: '', contacts: [] });
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', provider_company_id: '' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [providersRes, usersRes] = await Promise.all([
        providerCompaniesAPI.getAll(),
        usersAPI.getAll()
      ]);
      setProviders(providersRes.data);
      setAllUsers(usersRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProvider = async () => {
    if (!newProvider.name) {
      toast.error('El nombre es requerido');
      return;
    }

    try {
      await providerCompaniesAPI.create(newProvider);
      toast.success('Proveedor creado exitosamente');
      setShowCreateProvider(false);
      setNewProvider({ name: '', tax_id: '', contacts: [] });
      loadData();
    } catch (error) {
      toast.error('Error al crear proveedor');
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error('Todos los campos son requeridos');
      return;
    }

    try {
      // Get current user's account_id
      const currentUser = JSON.parse(localStorage.getItem('user'));
      
      await authAPI.register({
        account_id: currentUser.account_id,
        name: newUser.name,
        email: newUser.email,
        password: newUser.password,
        role: 'PROVIDER',
        provider_company_id: newUser.provider_company_id
      });
      
      toast.success('Usuario creado exitosamente');
      setShowCreateUser(false);
      setNewUser({ name: '', email: '', password: '', provider_company_id: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear usuario');
    }
  };

  const handleToggleUserActive = async (userId, currentStatus) => {
    try {
      await usersAPI.toggleActive(userId);
      toast.success(currentStatus ? 'Usuario desactivado' : 'Usuario activado');
      loadData();
    } catch (error) {
      toast.error('Error al actualizar usuario');
    }
  };

  const getUsersForProvider = (providerId) => {
    return allUsers.filter(u => u.provider_company_id === providerId);
  };

  const openCreateUser = (providerId) => {
    setNewUser({ ...newUser, provider_company_id: providerId });
    setSelectedProviderId(providerId);
    setShowCreateUser(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="providers-page">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Proveedores</h1>
          <p className="text-lg text-slate-600">Gestión de laboratorios y usuarios</p>
        </div>
        <Button onClick={() => setShowCreateProvider(true)} data-testid="create-provider-button">
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Proveedor
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 font-medium mb-1">Total Proveedores</p>
                <p className="text-3xl font-bold text-primary">{providers.length}</p>
              </div>
              <Building className="w-12 h-12 text-primary/30" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-transparent">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 font-medium mb-1">Total Usuarios</p>
                <p className="text-3xl font-bold text-blue-600">
                  {allUsers.filter(u => u.role === 'PROVIDER').length}
                </p>
              </div>
              <Users className="w-12 h-12 text-blue-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-transparent">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 font-medium mb-1">Usuarios Activos</p>
                <p className="text-3xl font-bold text-emerald-600">
                  {allUsers.filter(u => u.role === 'PROVIDER' && u.is_active).length}
                </p>
              </div>
              <Users className="w-12 h-12 text-emerald-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Providers List */}
      <div className="space-y-4">
        {providers.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Building className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 mb-4">No hay proveedores registrados</p>
              <Button onClick={() => setShowCreateProvider(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Crear Primer Proveedor
              </Button>
            </CardContent>
          </Card>
        ) : (
          providers.map((provider) => {
            const providerUsers = getUsersForProvider(provider.id);
            const isExpanded = expandedProvider === provider.id;

            return (
              <Card key={provider.id} className="border-slate-200 hover:shadow-md transition-shadow" data-testid={`provider-card-${provider.id}`}>
                <CardHeader className="cursor-pointer" onClick={() => setExpandedProvider(isExpanded ? null : provider.id)}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center">
                        <Building className="w-7 h-7 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-xl">{provider.name}</CardTitle>
                        <CardDescription>
                          {provider.tax_id && `RUC: ${provider.tax_id} • `}
                          {providerUsers.length} usuario{providerUsers.length !== 1 ? 's' : ''}
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className="text-sm">
                        <Users className="w-3 h-3 mr-1" />
                        {providerUsers.length}
                      </Badge>
                      {isExpanded ? (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                  </div>
                </CardHeader>

                {isExpanded && (
                  <CardContent className="border-t bg-slate-50">
                    <div className="pt-6 space-y-4">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold text-slate-900">Usuarios del Proveedor</h3>
                        <Button 
                          size="sm" 
                          onClick={(e) => {
                            e.stopPropagation();
                            openCreateUser(provider.id);
                          }}
                          data-testid={`add-user-${provider.id}`}
                        >
                          <UserPlus className="w-4 h-4 mr-2" />
                          Agregar Usuario
                        </Button>
                      </div>

                      {providerUsers.length === 0 ? (
                        <div className="text-center py-8 border-2 border-dashed border-slate-200 rounded-lg">
                          <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                          <p className="text-slate-500 text-sm mb-3">No hay usuarios para este proveedor</p>
                          <Button size="sm" variant="outline" onClick={() => openCreateUser(provider.id)}>
                            Agregar Primer Usuario
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {providerUsers.map((user) => (
                            <div
                              key={user.id}
                              className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-lg hover:border-primary/50 transition-colors"
                              data-testid={`user-card-${user.id}`}
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                  <span className="font-semibold text-primary text-sm">
                                    {user.name.charAt(0)}
                                  </span>
                                </div>
                                <div>
                                  <p className="font-medium text-slate-900">{user.name}</p>
                                  <p className="text-sm text-slate-600 flex items-center gap-1">
                                    <Mail className="w-3 h-3" />
                                    {user.email}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <Badge variant={user.is_active ? 'default' : 'secondary'}>
                                  {user.is_active ? 'Activo' : 'Inactivo'}
                                </Badge>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleToggleUserActive(user.id, user.is_active)}
                                  data-testid={`toggle-user-${user.id}`}
                                >
                                  {user.is_active ? (
                                    <ToggleRight className="w-5 h-5 text-emerald-600" />
                                  ) : (
                                    <ToggleLeft className="w-5 h-5 text-slate-400" />
                                  )}
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })
        )}
      </div>

      {/* Create Provider Dialog */}
      <Dialog open={showCreateProvider} onOpenChange={setShowCreateProvider}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuevo Proveedor</DialogTitle>
            <DialogDescription>Registra un nuevo laboratorio o proveedor</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nombre del Proveedor *</Label>
              <Input
                value={newProvider.name}
                onChange={(e) => setNewProvider({ ...newProvider, name: e.target.value })}
                placeholder="Ej: Bayer Ecuador"
                className="mt-1"
                data-testid="provider-name-input"
              />
            </div>
            <div>
              <Label>RUC / Tax ID</Label>
              <Input
                value={newProvider.tax_id}
                onChange={(e) => setNewProvider({ ...newProvider, tax_id: e.target.value })}
                placeholder="1790123456001"
                className="mt-1"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button onClick={handleCreateProvider} data-testid="submit-create-provider">
                Crear Proveedor
              </Button>
              <Button variant="outline" onClick={() => setShowCreateProvider(false)}>
                Cancelar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuevo Usuario de Proveedor</DialogTitle>
            <DialogDescription>Crea un usuario para que pueda acceder al sistema</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nombre Completo *</Label>
              <Input
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                placeholder="Juan Pérez"
                className="mt-1"
                data-testid="user-name-input"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="juan@bayer.ec"
                className="mt-1"
                data-testid="user-email-input"
              />
            </div>
            <div>
              <Label>Contraseña *</Label>
              <Input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                placeholder="••••••••"
                className="mt-1"
                data-testid="user-password-input"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button onClick={handleCreateUser} data-testid="submit-create-user">
                Crear Usuario
              </Button>
              <Button variant="outline" onClick={() => setShowCreateUser(false)}>
                Cancelar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
